import client from "./client";

export type AssignmentStatus =
  | "pending"
  | "viewed"
  | "signed"
  | "overdue"
  | "waived"
  | "expired";

export interface DocumentAssignment {
  id: string;
  org_id: string;
  template_id: string;
  employee_id: string;
  status: AssignmentStatus;
  assigned_at: string;
  deadline_at: string | null;
  viewed_at: string | null;
  completed_at: string | null;
  reminded_at: string | null;
  reminder_count: number;
  assigned_by: string;
  signed_document_url: string | null;
  waived_by: string | null;
  waived_reason: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  is_overdue: boolean;
  template?: {
    id: string;
    name: string;
    category: string | null;
    description: string | null;
    file_url: string | null;
    file_name: string | null;
    requires_signature: boolean;
    renewal_interval: string | null;
    /** ``file`` (PDF upload) or ``form`` (digital fillable form).
     *  Defaults to ``file`` on legacy rows. */
    template_type?: "file" | "form";
  } | null;
  employee?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    work_email: string | null;
  } | null;
}

export interface AssignmentSummary {
  total: number;
  pending: number;
  viewed: number;
  signed: number;
  overdue: number;
  waived: number;
  expired: number;
  percent_complete: number;
  by_category: {
    category: string;
    total: number;
    pending: number;
    signed: number;
    overdue: number;
  }[];
}

export async function listAssignments(params?: {
  employee_id?: string;
  template_id?: string;
  status?: AssignmentStatus;
  category?: string;
}): Promise<DocumentAssignment[]> {
  const res = await client.get<DocumentAssignment[]>(
    "/document-assignments",
    { params },
  );
  return res.data;
}

export async function getAssignment(
  id: string,
): Promise<DocumentAssignment> {
  const res = await client.get<DocumentAssignment>(
    `/document-assignments/${id}`,
  );
  return res.data;
}

export async function createAssignment(payload: {
  template_id: string;
  employee_id: string;
  deadline_days_override?: number | null;
  notes?: string | null;
}): Promise<DocumentAssignment> {
  const res = await client.post<DocumentAssignment>(
    "/document-assignments",
    payload,
  );
  return res.data;
}

export async function bulkCreateAssignments(payload: {
  template_ids: string[];
  employee_ids: string[];
  deadline_days_override?: number | null;
}): Promise<{
  created: number;
  skipped_existing: number;
  assignment_ids: string[];
}> {
  const res = await client.post("/document-assignments/bulk", payload);
  return res.data;
}

export async function updateAssignment(
  id: string,
  patch: {
    status?: AssignmentStatus;
    notes?: string;
    waived_reason?: string;
  },
): Promise<DocumentAssignment> {
  const res = await client.put<DocumentAssignment>(
    `/document-assignments/${id}`,
    patch,
  );
  return res.data;
}

export async function remindAssignment(
  id: string,
  message?: string,
): Promise<{ message: string; reminder_count: number }> {
  const res = await client.post(`/document-assignments/${id}/remind`, {
    message,
  });
  return res.data;
}

export async function getAssignmentSummary(): Promise<AssignmentSummary> {
  const res = await client.get<AssignmentSummary>(
    "/document-assignments/summary",
  );
  return res.data;
}

// ── Manager: team-scoped overdue ─────────────────────────────────

/**
 * Lists document assignments overdue for the caller's direct reports.
 * Set is guaranteed to match the count in
 * ``GET /my/team/alerts`` (code=overdue_documents) — both endpoints
 * share the same underlying query on the backend.
 *
 * Returns ``[]`` when the caller isn't backed by an Employee row
 * (e.g. service users) or has no overdue reports.
 */
export async function listTeamOverdueDocuments(): Promise<
  DocumentAssignment[]
> {
  const res = await client.get<DocumentAssignment[]>(
    "/document-assignments/team-overdue",
  );
  return res.data;
}

// ── Employee self-service ────────────────────────────────────────

export async function listMyDocuments(
  status?: AssignmentStatus,
): Promise<DocumentAssignment[]> {
  const res = await client.get<DocumentAssignment[]>(
    "/document-assignments/my/documents",
    { params: status ? { status } : undefined },
  );
  return res.data;
}

/**
 * Single-row fetch for the document detail/viewer page. Server stamps
 * viewed_at on first open so the dashboard's "viewed" count stays in
 * sync with the deep-link flow.
 */
export async function getMyDocument(id: string): Promise<DocumentAssignment> {
  const res = await client.get<DocumentAssignment>(
    `/document-assignments/my/documents/${id}`,
  );
  return res.data;
}

export async function acknowledgeAssignment(
  id: string,
): Promise<DocumentAssignment> {
  const res = await client.post<DocumentAssignment>(
    `/document-assignments/${id}/acknowledge`,
    {},
  );
  return res.data;
}

export async function signAssignment(
  id: string,
  signed_document_url?: string,
): Promise<DocumentAssignment> {
  const res = await client.post<DocumentAssignment>(
    `/document-assignments/${id}/sign`,
    { signed_document_url },
  );
  return res.data;
}


// ── Digital form submission ─────────────────────────────────────────

export interface FormSubmissionView {
  assignment_id: string;
  status: AssignmentStatus | "reviewed" | "revision_requested";
  template_name: string;
  template_description: string | null;
  form_schema: Array<Record<string, unknown>>;
  /** Submitted/draft data with SSN values masked. */
  form_data: Record<string, unknown>;
  form_submitted_at: string | null;
  form_pdf_url: string | null;
  /** When the admin requested a revision, the note they left. The
   *  employee form-fill page surfaces this in a yellow banner. */
  revision_note?: string | null;
  reviewed_at?: string | null;
}

export async function getMyForm(
  assignmentId: string,
): Promise<FormSubmissionView> {
  const res = await client.get<FormSubmissionView>(
    `/document-assignments/my/documents/${assignmentId}/form`,
  );
  return res.data;
}

export async function saveMyFormDraft(
  assignmentId: string,
  data: Record<string, unknown>,
): Promise<void> {
  await client.post(
    `/document-assignments/my/documents/${assignmentId}/form/save-draft`,
    { data },
  );
}

export async function submitMyForm(
  assignmentId: string,
  data: Record<string, unknown>,
): Promise<{ status: string; form_pdf_url: string | null }> {
  const res = await client.post(
    `/document-assignments/my/documents/${assignmentId}/form/submit`,
    { data },
  );
  return res.data;
}

// Admin review surface

export interface AdminFormSubmissionView {
  assignment_id: string;
  status: AssignmentStatus | "reviewed" | "revision_requested";
  employee_id: string;
  template_id: string;
  template_name: string;
  form_schema: Array<Record<string, unknown>>;
  /** SSN values come back masked unless the caller explicitly hits
   *  the dedicated reveal endpoint (audit-logged). */
  form_data: Record<string, unknown>;
  form_submitted_at: string | null;
  form_ip_address: string | null;
  form_pdf_url: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  revision_note: string | null;
}

export async function getAdminFormSubmission(
  assignmentId: string,
): Promise<AdminFormSubmissionView> {
  const res = await client.get<AdminFormSubmissionView>(
    `/document-assignments/${assignmentId}/form`,
  );
  return res.data;
}

export async function markFormReviewed(
  assignmentId: string,
): Promise<{ status: string; reviewed_at: string }> {
  const res = await client.post(
    `/document-assignments/${assignmentId}/form/mark-reviewed`,
  );
  return res.data;
}

export async function requestFormRevision(
  assignmentId: string,
  note: string,
): Promise<{ status: string; revision_note: string }> {
  const res = await client.post(
    `/document-assignments/${assignmentId}/form/request-revision`,
    { note },
  );
  return res.data;
}

export interface RecentFormSubmission {
  assignment_id: string;
  employee_id: string;
  employee_name: string;
  template_name: string;
  status: string;
  form_submitted_at: string | null;
  reviewed_at: string | null;
  is_reviewed: boolean;
}

export async function listRecentFormSubmissions(
  limit = 5,
): Promise<RecentFormSubmission[]> {
  const res = await client.get<RecentFormSubmission[]>(
    "/document-assignments/forms/recent-submissions",
    { params: { limit } },
  );
  return res.data;
}
